import type { UnitsSystem } from '@innovyze/stylovyze';
import type { FilterFieldValue } from '@Types/deteriorationModels.types';
import type { AssetDiameterBreakDown } from '@Types/failureGrade.types';

// Server data is metric: pipe length is stored in metres (LENGTH_LONG) and
// diameter in millimetres (LENGTH). Conversion to the tenant's Admin display
// unit happens here, at the edge, never in the stored value.
const METRES_PER_KILOMETRE = 1000;
const METRES_PER_MILE = 1609.344;

// The API compares diameter as $round(diameter, 1) for =, !=, <, <=, > and >=,
// so a filter value must arrive at one decimal place or an equality filter never
// matches: 6" is 152.39999999999998 mm unrounded. (isAnyOf is an exact $in on the
// API side with no rounding, so it stays approximate - see the PR notes.)
const DIAMETER_FILTER_DECIMALS = 1;
const DIAMETER_STORAGE_UNIT = 'mm';

// Grade-card totals are shown to two decimals. Formatted with the browser locale,
// exactly as stylovyze's toDecimalPlaces does for the table cells, so separators
// agree across the screen.
const totalFormatter = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const SMALLEST_SHOWN_TOTAL = 0.01;

/**
 * Format a summed pipe length for the grade cards, in km or miles.
 *
 * The unit system's own length conversion stops at metres/feet, which reads
 * poorly for network totals (hundreds of thousands of feet), so the summary
 * cards use the long-distance unit for the tenant's system instead.
 */
export const formatTotalLength = (metres: number, system: UnitsSystem): string => {
	const imperial = system === 'Imperial';
	const unit = imperial ? 'mi' : 'km';
	if (metres === 0) return `0 ${unit}`;

	const value = metres / (imperial ? METRES_PER_MILE : METRES_PER_KILOMETRE);
	// A non-zero total that would round to 0.00 reads as "no length at all", so
	// say it is below the smallest value shown instead.
	if (value < SMALLEST_SHOWN_TOTAL / 2) return `< ${totalFormatter.format(SMALLEST_SHOWN_TOTAL)} ${unit}`;
	return `${totalFormatter.format(value)} ${unit}`;
};

/**
 * Convert a diameter filter from the tenant's display unit back to millimetres.
 *
 * The grid shows diameters in the display unit, so an imperial user types
 * inches; the API compares against stored millimetres. Without this, finding an
 * 8" pipe means typing 203.2. Mirrors updateImperialEntries in app-am-activities:
 * only imperial tenants are converted, since a metric tenant already types mm.
 *
 * `convertToMetric` is injected (stylovyze's convertToMetricNoIntlFormat in the
 * component) so this stays testable without loading the stylovyze bundle. The
 * precision and target unit are fixed here rather than left to the caller: both
 * are required for the API comparison, and the converter defaults to neither.
 */
export const diameterFilterToMetric = (
	entry: FilterFieldValue,
	system: UnitsSystem,
	displayUnit: string | undefined,
	convertToMetric: (measurement: string, decimals: number, metricUnit: string) => string,
): FilterFieldValue => {
	if (system !== 'Imperial' || !displayUnit || entry.value == null) return entry;

	const toMetric = <T>(value: T): T | string => {
		// Leave absent values alone: String(null) is "null", which is not a diameter.
		if (value == null || value === '') return value;
		const text = String(value);
		try {
			return convertToMetric(`${text} ${displayUnit}`, DIAMETER_FILTER_DECIMALS, DIAMETER_STORAGE_UNIT);
		} catch {
			// The converter throws on input it cannot parse. Letting that escape would
			// abort the grid's filter-change handler and silently drop the filter, so
			// pass the typed value through and let the API reject or ignore it.
			return text;
		}
	};

	return {
		...entry,
		value: Array.isArray(entry.value) ? entry.value.map(value => toMetric(value)) : toMetric(entry.value),
	};
};

/**
 * Build pie slices for the diameter breakdown, labelled in the display unit.
 *
 * The server buckets by millimetre value, so labels are converted for display.
 * Distinct millimetre buckets can round to the same display label (150.0 and
 * 149.9 mm both read "5.9 in"); those are merged so the pie never shows two
 * slices with the same name. Non-numeric buckets such as "Unknown" pass through.
 */
export const diameterSlicesForDisplay = (
	rawData: AssetDiameterBreakDown,
	formatDiameter: (millimetres: string) => string,
	// Buckets to include, in slice order; any absent from rawData become 0-count slices.
	// Defaults to rawData's own buckets. Passed explicitly because Object.keys puts
	// integer-like keys ("8") first whatever order they were added in.
	millimetreKeys: string[] = Object.keys(rawData),
): [string, number][] => {
	const counts = new Map<string, number>();
	millimetreKeys.forEach(millimetres => {
		// Number('') is 0, so an empty key would otherwise be labelled as a 0 diameter.
		const isNumeric = millimetres.trim() !== '' && Number.isFinite(Number(millimetres));
		const label = isNumeric ? formatDiameter(millimetres) : millimetres;
		counts.set(label, (counts.get(label) ?? 0) + (rawData[millimetres]?.count || 0));
	});
	return Array.from(counts.entries());
};
